<template>
  <div>
    <vue-headful :title="title" />
    <div class="mx-4 my-2" v-if="terminal">
      <div class="card" v-if="terminal?.activeSubscription">
        <div class="card-content">
          <div class="columns">
            <div class="column is-one-quarter is-flex is-align-items-center">
              <template v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
                <div class="buttons">
                  <b-button type="is-primary is-outlined" icon-left="cloud-upload-alt" :disabled="isSyncDisabled" @click="confirmSync">Synchronize</b-button>
                  <b-button type="is-outlined" icon-left="sync" @click="refreshProviderState" :loading="isLoadingProviderState">Refresh</b-button>
                </div>
              </template>
            </div>
            <div class="column is-three-quarters">
              <b-message type="is-warning" v-if="isSyncDisabled">
                <ul>
                  <li v-for="(error, idx) in errors" :key="idx">
                    {{error}}
                  </li>
                </ul>
              </b-message>
              <b-message type="is-info" v-if="!isSyncDisabled && terminal.syncInProgress">
                Terminal sync is currently in progress.
              </b-message>
              <b-message type="is-success" v-if="!isSyncDisabled && !terminal.syncInProgress">
                Terminal is ready to be synchronized.
              </b-message>
            </div>
          </div>
        </div>
      </div>
      <div class="card mt-4" v-if="terminal?.activeSubscription">
        <div class="card-header">
          <p class="card-header-title">Terminal Hub State</p>
        </div>
        <div class="card-content">
          <TerminalHubStateComponent />
        </div>
      </div>
      <div class="card mt-4">
        <div class="card-header">
          <p class="card-header-title">Satellite Network Configuration</p>
          <button class="card-header-icon" aria-label="more options">
            <span class="icon" @click="satNetConfToggledOn = !satNetConfToggledOn">
              <font-awesome-icon :icon="['far', 'angle-down']" v-if="satNetConfToggledOn" />
              <font-awesome-icon :icon="['far', 'angle-up']" v-if="!satNetConfToggledOn" />
            </span>
          </button>
        </div>
        <div class="card-content" v-if="satNetConfToggledOn">
          <TerminalNetworkConfigurationComponent />
        </div>
      </div>
      <div class="card mt-4" v-if="terminal?.activeSubscription">
        <div class="card-header">
          <p class="card-header-title">Network Inventory</p>
          <button class="card-header-icon" aria-label="more options">
            <span class="icon" @click="networkInventoryToggledOn = !networkInventoryToggledOn">
              <font-awesome-icon :icon="['far', 'angle-down']" v-if="networkInventoryToggledOn" />
              <font-awesome-icon :icon="['far', 'angle-up']" v-if="!networkInventoryToggledOn" />
            </span>
          </button>
        </div>
        <div class="card-content" v-if="networkInventoryToggledOn">
          <TerminalNetworkInventoryComponent />
        </div>
      </div>
      <div class="card mt-4">
        <div class="card-header">
          <p class="card-header-title">SIM Configuration</p>
          <button class="card-header-icon" aria-label="more options">
            <span class="icon" @click="simConfToggledOn = !simConfToggledOn">
              <font-awesome-icon :icon="['far', 'angle-down']" v-if="simConfToggledOn" />
              <font-awesome-icon :icon="['far', 'angle-up']" v-if="!simConfToggledOn" />
            </span>
          </button>
        </div>
        <div class="card-content" v-if="terminal?.simConfiguration && simConfToggledOn">
          <detail-field-2>
            <template v-slot:label>EID</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.eid }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>MSISDN</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.msisdn }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Cubic LTE IMSI</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.cubicLTEIMSI }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Tele2 LTE IMSI</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.tele2LTEIMSI }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Cubic DK IMSI</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.cubicDkIMSI }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Cubic FR IMSI</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.cubicFrIMSI }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Batch</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.batch }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Profile</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.profile }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Form Factor</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.formFactor }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Grade</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.grade }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>SIM Vendor</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.simVendor }}</template>
          </detail-field-2>
          <detail-field-2>
            <template v-slot:label>Checked out to</template>
            <template v-slot:value>{{ terminal?.simConfiguration?.checkedOutTo }}</template>
          </detail-field-2>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref, getCurrentInstance } from 'vue';
import { useTerminalsStore } from '../terminals-store';
import TerminalNetworkConfigurationComponent from '../components/TerminalNetworkConfigurationComponent.vue';
import TerminalNetworkInventoryComponent from '../components/TerminalNetworkInventoryComponent.vue';
import TerminalHubStateComponent from '../components/TerminalHubStateComponent.vue';

const store = useTerminalsStore();
const app = getCurrentInstance();
const terminal = computed(() => store.state.terminal);
const hubState = computed(() => store.state.hubState);
const title = computed(() => `${terminal.value?.name} / Configuration`);
const satNetConfToggledOn = ref(false);
const simConfToggledOn = ref(false);
const networkInventoryToggledOn = ref(false);

const refreshProviderState = async () => {
  await store.dispatch('fetchTerminalHubState');
}
const isLoadingProviderState = computed(() => store.state.hubStateLoading);

// #region Sync Button
const confirmSync = () => {
  app.proxy.$buefy.dialog.confirm({
    title: 'Confirm Terminal Sync',
    message: 'The Sync process will update network provider state to match Kymeta configured state. This process proceeds in the background, and may take several minutes. Are you sure you would like to do this?',
    cancelText: 'No',
    confirmText: 'Yes',
    type: 'is-warning',
    onConfirm: async () => {
      const result = await store.dispatch('executeTerminalCommand', { id: terminal?.value?.id, cmd: 'syncToHub' });
      if (!result) {
        app.proxy.$buefy.toast.open({
          duration: 2500,
          message: 'Error queueing a sync to hub.',
          type: 'is-danger'
        });
      }
    }
  })
}

const hasValidNetworkConfig = computed(() => {
  const evolutionSubscription = terminal?.value?.activeSubscription?.networkIdentifiers?.includes('Evolution');
  if (!evolutionSubscription) return true;
  // if subscription have EVO / Lepton, check for Valid NetworkConfiguration > IP Addresses
  return terminal?.value?.satelliteNetworkConfiguration?.eth0IpAddress
    && terminal?.value?.satelliteNetworkConfiguration?.eth0SubnetMask
    && terminal?.value?.satelliteNetworkConfiguration?.sat0IpAddress
    && terminal?.value?.satelliteNetworkConfiguration?.sat0SubnetMask
    && terminal?.value?.satelliteNetworkConfiguration?.mgmtIpAddress
    && terminal?.value?.satelliteNetworkConfiguration?.mgmtSubnetMask
    && terminal?.value?.satelliteNetworkConfiguration?.dhcpGateway
    && terminal?.value?.satelliteNetworkConfiguration?.dhcpClientIpRangeStart
    && terminal?.value?.satelliteNetworkConfiguration?.dhcpClientIpRangeEnd
});
const devicesMissingErrors = computed(() => {
  const errors = [];
  if (!terminal?.value?.activeSubscription) errors.push('Terminal does not have an active subscription.');
  if (!terminal?.value?.hardware?.antenna?.model) errors.push('Terminal does not have an antenna model defined.');
  if (!terminal?.value?.hardware?.geoModem?.serial) errors.push('Terminal does not have a modem serial defined.');
  if (!terminal?.value?.hardware?.geoModem?.model) errors.push('Terminal does not have a modem model defined.');
  if (!terminal?.value?.hardware?.buc?.model) errors.push('Terminal does not have a BUC model defined.');
  if (!hasValidNetworkConfig.value) errors.push('Terminal does not have a valid network configuration (IP Addresses).');
  return errors;
});
const errors = computed(() => {
  const e = [...devicesMissingErrors.value];
  if (terminal?.value?.syncInProgress) e.push('Terminal does not have an active subscription.');
  if (!hubState?.value) e.push('Could not retrieve current NMS hub state.');
  if (!terminal?.value?.activeSubscription) e.push('Terminal does not have an active subscription.');
  return e;
});
const preSyncErrors = computed(() => devicesMissingErrors.value.length != 0);
const isSyncDisabled = computed(() => (terminal?.value?.syncInProgress) || (!hubState?.value) || (!terminal?.value?.activeSubscription) || (preSyncErrors.value));
// #endregion
</script>
